import React from 'react';
import { Link } from 'react-router-dom';
import {ArrowRight} from 'lucide-react';

export default function ServicesPage() {
  const services = [
    {
      id: 'dot-specimen',
      name: 'DOT & Non-DOT Specimen Collection',
      icon: '🧪',
      description: 'Professional specimen collection services compliant with Department of Transportation regulations and standard testing protocols.',
      features: ['DOT-qualified collectors', 'Chain of custody procedures', 'Immediate processing', 'Nationwide network']
    },
    {
      id: 'dna-testing',
      name: 'DNA Testing',
      icon: '🧬',
      description: 'Accurate and confidential DNA testing services for paternity, legal, and personal identification purposes.',
      features: ['Legal paternity testing', 'Immigration DNA testing', 'Relationship testing', 'Confidential results']
    },
    {
      id: 'blood-testing',
      name: 'Blood Testing',
      icon: '💉',
      description: 'Comprehensive blood analysis services including routine panels, specialized tests, and health screenings.',
      features: ['Complete blood count', 'Metabolic panels', 'Hormone testing', 'Disease screening']
    },
    {
      id: 'urine-drug-testing',
      name: 'Urine Drug Testing',
      icon: '🔬',
      description: 'Reliable drug screening services for employment, legal, and personal purposes with fast turnaround times.',
      features: ['5-panel to 12-panel tests', 'Same-day results available', 'DOT & Non-DOT testing', 'Confirmation testing']
    },
    {
      id: 'flu-testing',
      name: 'Flu Testing',
      icon: '🦠',
      description: 'Rapid influenza diagnostic testing to quickly identify flu infections and guide treatment decisions.',
      features: ['Rapid results (15 minutes)', 'Influenza A & B detection', 'Walk-in appointments', 'Accurate diagnostics']
    },
    {
      id: 'fingerprint',
      name: 'Electronic Fingerprint',
      icon: '👆',
      description: 'Digital fingerprinting services for background checks, employment, licensing, and legal requirements.',
      features: ['FBI background checks', 'State fingerprinting', 'Licensing requirements', 'Adoption services']
    },
    {
      id: 'i9-checks',
      name: 'I-9 Background Checks',
      icon: '📋',
      description: 'Employment eligibility verification and comprehensive background check services for employers.',
      features: ['I-9 form verification', 'Employment screening', 'Credential verification', 'Compliance support']
    },
    {
      id: 'ctpa',
      name: 'C/TPA Services',
      icon: '🏥',
      description: 'Consortium/Third Party Administrator services for drug and alcohol testing programs management.',
      features: ['DOT compliance management', 'Random testing programs', 'Policy development', 'Reporting services']
    }
  ];

  return (
    <div>
      {/* Header */}
      <section className="bg-gradient-to-r from-blue-600 to-teal-600 text-white py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.pexels.com/photos/8460300/pexels-photo-8460300.jpeg?auto=compress&cs=tinysrgb&w=1920" 
            alt="Diverse team of medical laboratory professionals in modern diagnostic facility"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
          <p className="text-xl text-blue-50 max-w-3xl">
            Comprehensive laboratory and diagnostic services delivered with accuracy, confidentiality, and care.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="p-8">
                  <div className="text-5xl mb-4" aria-hidden="true">{service.icon}</div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">{service.name}</h2>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Key Features:</h3>
                    <ul className="space-y-2">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-700">
                          <span className="text-teal-600 mt-1">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={`/services/${service.id}`}
                    className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                  >
                    Learn More <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Need Help Choosing a Service?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Our experienced staff is here to guide you through the testing process and answer any questions you may have.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/appointment"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Book Appointment <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
